/**
 * Обмены между рублёвым счётом и картой (Flow steps "card_topup" / "card_refund").
 *
 * CardTopUp  — рубли со счёта → доллары на карту по курсу с главной (базовый + наценка).
 * CardRefund — доллары с карты → рубли на счёт по курсу «базовый − скидка» (задаётся в админке).
 * В обе стороны сверх курса удерживается комиссия в процентах (одна настройка на оба
 * направления). Пересчёт «получите» считает сервер (quote) — фронт не дублирует формулу.
 *
 * Обмен мгновенный, без заявки: после успеха проигрывается анимация перелёта суммы (вверх —
 * на карту, вниз — на счёт) и экран сам возвращает в кабинет. Пропсы: card, accountRub,
 * config, onDone (обновить кабинет), onBack.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useBackButton } from "@/telegram/useMainButton";
import { Screen } from "@/components/Screen";
import { Field } from "@/components/Field";
import { MaxButton } from "@/components/MaxButton";
import { Note } from "@/components/Note";
import { ApiError, cardsApi, friendlyError, pollCardOperation, type CardConfig, type IssuedCard } from "@/lib/api";
import { formatRub } from "@/lib/format";
import { CardFace } from "./CardFace";

type Mode = "topup" | "refund";

type Props = {
  card: IssuedCard;
  accountRub: number;
  config: CardConfig;
  onDone: () => void;
  onBack: () => void;
};

function errorText(e: unknown, fallback: string): string {
  if (e instanceof ApiError) {
    switch (e.detail) {
      case "insufficient_rub":
        return "На счёте недостаточно рублей.";
      case "insufficient_usd":
        return "На карте недостаточно средств.";
      case "below_min":
        return "Сумма меньше минимальной для обмена.";
      case "above_max":
        return "Сумма больше максимальной для обмена.";
      case "bad_amount":
        return "Слишком маленькая сумма.";
      case "no_card":
        return "Карта не выпущена.";
    }
  }
  return friendlyError(e, fallback);
}

function Exchange({ mode, card, accountRub, config, onDone, onBack }: Props & { mode: Mode }) {
  const topup = mode === "topup";
  const [amount, setAmount] = useState("");
  const [net, setNet] = useState<number | null>(null);   // сколько получит клиент
  const [feeAmt, setFeeAmt] = useState(0);
  const [quoting, setQuoting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pending, setPending] = useState(false);   // операция принята, «зачисляется» у брокера
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const timer = useRef<number | null>(null);

  const num = Number(amount.replace(",", "."));
  const available = topup ? accountRub : card.balance_usd;
  // «МАКС»: весь остаток, но не больше лимита одной операции (лимит задан в рублях,
  // поэтому применяем его только к пополнению со счёта). Округляем вниз до копеек.
  const maxAmount = Math.floor((topup ? Math.min(available, config.max_rub) : available) * 100) / 100;
  const rate = topup ? config.buy_rate : config.sell_rate;
  const enough = num > 0 && num <= available;
  const limitOk = !topup || (num >= config.min_rub && num <= config.max_rub);
  const valid = enough && limitOk && rate != null && !submitting;

  // предпросмотр считает сервер: у него курс, комиссия и правила округления
  useEffect(() => {
    if (!(num > 0)) {
      setNet(null);
      return;
    }
    let alive = true;
    setQuoting(true);
    const t = setTimeout(() => {
      const q = topup ? cardsApi.quoteTopup(num) : cardsApi.quoteRefund(num);
      q.then((r) => {
        if (!alive) return;
        setNet(topup ? (r as { net_usd: number }).net_usd : (r as { net_rub: number }).net_rub);
        setFeeAmt(topup ? (r as { fee_usd: number }).fee_usd : (r as { fee_rub: number }).fee_rub);
      })
        .catch(() => alive && setNet(null))
        .finally(() => alive && setQuoting(false));
    }, 250);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [num, topup]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const submit = useCallback(async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = topup ? await cardsApi.topup(num) : await cardsApi.refund(num);
      let op = res.operation;
      // synthetic: обмен у брокера асинхронный → операция «зачисляется», ждём завершения
      if (op.status === "processing" || op.status === "pending") {
        setPending(true);
        const final = await pollCardOperation(op.id);
        if (final) op = final;
      }
      if (op.status === "failed") {
        setError(op.error === "service_unavailable"
          ? "Сервис временно недоступен. Попробуйте чуть позже."
          : "Не удалось выполнить обмен. Средства не списаны.");
        setPending(false);
        return;
      }
      if (op.status === "processing" || op.status === "pending") {
        // не дождались за таймаут — не ошибка: обмен продолжится, кабинет обновит статус
        setError("Операция ещё зачисляется — обновите кабинет через минуту.");
        setPending(false);
        return;
      }
      setPending(false);
      setDone(true);
      timer.current = window.setTimeout(onDone, 1500);  // даём доиграть анимации
    } catch (e) {
      setError(errorText(e, "Не удалось выполнить обмен"));
      setPending(false);
    } finally {
      setSubmitting(false);
    }
  }, [valid, submitting, topup, num, onDone]);

  useBackButton(onBack);

  const amountErr =
    amount && !enough
      ? topup
        ? `Не больше баланса счёта — ${formatRub(available)}`
        : `Не больше баланса карты — $${available.toFixed(2)}`
      : amount && !limitOk
        ? `Сумма от ${config.min_rub.toLocaleString("ru-RU")} до ${config.max_rub.toLocaleString("ru-RU")} ₽`
        : undefined;

  return (
    <Screen
      title={topup ? "Пополнение карты" : "Возврат на счёт"}
      subtitle={topup ? "Со счёта в рублях на карту в долларах" : "С карты в долларах на счёт в рублях"}
    >
      {/* карта сверху: видно, куда (или откуда) уходят деньги */}
      <div className={`relative mx-auto mb-5 w-full max-w-[20rem] ${done ? "animate-card-pulse" : ""}`}>
        <CardFace card={card} />
        {/* сумма перелетает: вверх — на карту, вниз — на счёт */}
        {done && (
          <div
            className={`pointer-events-none absolute left-1/2 -translate-x-1/2 text-[17px] font-semibold text-brand ${
              topup ? "-bottom-6 animate-card-fly-up" : "-top-6 animate-card-fly-down"
            }`}
          >
            {topup ? `+ $${(net ?? 0).toFixed(2)}` : `+ ${formatRub(net ?? 0)}`}
          </div>
        )}
      </div>

      <div className="space-y-3">
        <Field
          label={topup ? "Сумма со счёта" : "Сумма с карты"}
          type="text"
          inputMode="decimal"
          autoFocus
          placeholder="0"
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^\d.,]/g, ""))}
          suffix={
            <span className="flex items-center gap-2.5">
              <span className="text-[14px] text-white/45">{topup ? "₽" : "USD"}</span>
              <MaxButton onClick={() => setAmount(maxAmount > 0 ? String(maxAmount) : "")} />
            </span>
          }
          error={amountErr}
          hint={
            !amountErr
              ? topup
                ? `Доступно: ${formatRub(available)}`
                : `Доступно на карте: $${available.toFixed(2)}`
              : undefined
          }
        />
      </div>

      <Note className="mt-4">
        {rate == null ? (
          "Курс сейчас недоступен — обмен временно невозможен."
        ) : (
          <>
            Курс: {rate.toFixed(2)} ₽ за $1
            {config.fee_pct > 0 ? ` · комиссия ${config.fee_pct}%` : " · без комиссии"}.{" "}
            {num > 0 && (
              <>
                К зачислению:{" "}
                <span className="font-medium text-white">
                  {quoting || net == null
                    ? "…"
                    : topup
                      ? `$${net.toFixed(2)}`
                      : formatRub(net)}
                </span>
                {feeAmt > 0 && (
                  <span className="text-white/45">
                    {" "}
                    (удержано {topup ? `$${feeAmt.toFixed(2)}` : formatRub(feeAmt)})
                  </span>
                )}
              </>
            )}
          </>
        )}
      </Note>

      {error && (
        <Note variant="danger" className="mt-3">
          {error}
        </Note>
      )}

      <button
        onClick={submit}
        disabled={!valid || done || pending}
        className="mt-5 w-full rounded-2xl bg-brand px-4 py-3.5 text-[15px] font-semibold text-surface-base shadow-lg shadow-brand/25 active:brightness-95 disabled:opacity-40"
      >
        {done ? "Готово" : pending ? "Зачисляется…" : submitting ? "Выполняем…" : topup ? "Пополнить карту" : "Вернуть на счёт"}
      </button>
    </Screen>
  );
}

export function CardTopUp(props: Props) {
  return <Exchange mode="topup" {...props} />;
}

export function CardRefund(props: Props) {
  return <Exchange mode="refund" {...props} />;
}
